import React, { useState, useEffect, useRef } from 'react'
import { ChatIcon, SparklesIcon, ArrowLeftIcon } from '@heroicons/react/outline'
import { useGlobalState } from '@/state'
import { createConsumer } from "@rails/actioncable"
import { useLocation, useHistory } from 'react-router-dom'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import Card from '@/shared/Card'
import SocialPostForm from './Social/SocialPostForm'
import SocialPost from './Social/SocialPost'
import SocialReplies from './Social/SocialReplies'

const SocialIndex = () => {
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const location = useLocation()
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [posts, setPosts] = useState([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [singlePost, setSinglePost] = useState(null)
  const [replies, setReplies] = useState([])
  const scrollRef = useRef(null)

  // Check for post_id parameter in URL
  const urlParams = new URLSearchParams(location.search)
  const postId = urlParams.get('post_id')
  const isSinglePostView = !!postId

  useEffect(() => {
    if (isSinglePostView) {
      fetchSinglePost(postId)
    } else {
      fetchTimeline()
    }
    
    // Set up ActionCable subscription
    const cable = createConsumer()
    const subscription = cable.subscriptions.create('SocialTimelineChannel', {
      received: (data) => {
        handleRealTimeUpdate(data)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [postId])

  const fetchTimeline = (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true)
    
    getRequest('/api/v3/social_posts/timeline', { page: pageNum }, (err, response) => {
      setLoading(false)
      if (err) return
      
      if (append) {
        setPosts(prev => [...prev, ...response])
      } else {
        setPosts(response)
      }
      
      setHasMore(response.length === 20) // Assuming 20 per page
      setPage(pageNum)
    })
  }

  const fetchSinglePost = (id) => {
    setLoading(true)
    
    getRequest(`/api/v3/social_posts/${id}`, {}, (err, response) => {
      setLoading(false)
      if (err) {
        console.error('Error fetching single post:', err)
        return
      }
      
      setSinglePost(response.post)
      setReplies(response.replies || [])
    })
  }

  const handleRealTimeUpdate = (data) => {
    if (isSinglePostView) {
      // Handle real-time updates for single post view
      switch (data.action) {
        case 'update_post':
          if (singlePost && singlePost.id === data.post.id) {
            setSinglePost(data.post)
          }
          break
        case 'delete_post':
          if (singlePost && singlePost.id === data.post_id) {
            // Post was deleted, redirect to timeline
            goBackToTimeline()
          }
          break
        case 'update_reactions':
          if (singlePost && singlePost.id === data.post_id) {
            setSinglePost(prev => ({
              ...prev,
              reactions_count: data.reactions_count,
              reaction_counts: data.reaction_counts,
              user_reaction: data.user_reaction
            }))
          }
          break
        case 'new_reply':
          if (singlePost && singlePost.id === data.post_id) {
            setSinglePost(prev => ({
              ...prev,
              replies_count: data.replies_count
            }))
            // Add the new reply to the replies list if it's provided
            if (data.reply) {
              setReplies(prev => [data.reply, ...prev])
            }
          }
          break
      }
    } else {
      // Handle real-time updates for timeline view
      switch (data.action) {
        case 'new_post':
          setPosts(prev => [data.post, ...prev])
          break
        case 'update_post':
          setPosts(prev => prev.map(post => 
            post.id === data.post.id ? data.post : post
          ))
          break
        case 'delete_post':
          setPosts(prev => prev.filter(post => post.id !== data.post_id))
          break
        case 'update_reactions':
          setPosts(prev => prev.map(post => 
            post.id === data.post_id ? {
              ...post,
              reactions_count: data.reactions_count,
              reaction_counts: data.reaction_counts,
              user_reaction: data.user_reaction
            } : post
          ))
          break
        case 'new_reply':
          setPosts(prev => prev.map(post => 
            post.id === data.post_id ? {
              ...post,
              replies_count: data.replies_count
            } : post
          ))
          break
      }
    }
  }

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev])
  }

  const handlePostUpdate = (updatedPost) => {
    setPosts(prev => prev.map(post => 
      post.id === updatedPost.id ? updatedPost : post
    ))
  }

  const handlePostDelete = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId))
  }

  const loadMore = () => {
    if (!hasMore || loading) return
    fetchTimeline(page + 1, true)
  }

  const goBackToTimeline = () => {
    history.push('/social')
  }

  const handleReplyCreated = (newReply) => {
    if (isSinglePostView) {
      setReplies(prev => [newReply, ...prev])
      if (singlePost) {
        setSinglePost(prev => ({
          ...prev,
          replies_count: prev.replies_count + 1
        }))
      }
    }
  }

  return (
    <div className='mt-3 max-w-2xl mx-auto'>
      <Card 
        title={
          <div className='flex items-center space-x-2'>
            {isSinglePostView ? (
              <>
                <ArrowLeftIcon 
                  className="h-6 w-6 text-cccorange dark:text-cccpurple cursor-pointer" 
                  onClick={goBackToTimeline}
                />
                <span className='font-medium leading-6 text-cccorange dark:text-cccpurple'>All Posts</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-6 w-6 text-cccorange dark:text-cccpurple" />
                <span className='font-medium leading-6 text-cccorange dark:text-cccpurple'>Social Timeline</span>
              </>
            )}
          </div>
        }
      >
        <div ref={scrollRef}>
          {isSinglePostView ? (
            // Single post view
            loading ? (
              <Loading />
            ) : singlePost ? (
              <div className="space-y-4">
                <SocialPost
                  post={singlePost}
                  onPostUpdate={handlePostUpdate}
                  onPostDelete={handlePostDelete}
                  showFullReplies={true}
                  initialReplies={replies}
                  onReplyCreated={handleReplyCreated}
                />
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">Post not found</p>
              </div>
            )
          ) : (
            // Timeline view
            <>
              {/* Privacy note */}
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <span className="font-medium">Privacy:</span> You see posts from people you follow. Only your followers can see your posts.
                </p>
              </div>
              
              {/* Post creation form */}
              <SocialPostForm onPostCreated={handlePostCreated} />
              
              {/* Timeline */}
              {loading && posts.length === 0 ? (
                <Loading />
              ) : posts.length === 0 ? (
                <NoResults />
              ) : (
                <div className="space-y-0">
                  {posts.map((post) => (
                    <SocialPost
                      key={post.id}
                      post={post}
                      onPostUpdate={handlePostUpdate}
                      onPostDelete={handlePostDelete}
                    />
                  ))}
                  
                  {hasMore && (
                    <div className="text-center py-4">
                      <button
                        onClick={loadMore}
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        {loading ? 'Loading...' : 'Load More'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

const NoResults = () => {
  return (
    <div className="relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccblue dark:border-gray-600">
      <ChatIcon className="mx-auto h-12 w-12 text-gray-400" />
      <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300">
        Your timeline is empty! Follow some users or create your first post to get started.
      </span>
    </div>
  )
}

export default SocialIndex
